import { useEffect, useState, useCallback } from 'react';
import { ErrorBoundary } from '../shared/ErrorBoundary';
import { usePlayerListContext } from '../../lib/player-list.context';
import { Loader2, Trash2, GripVertical, AlertCircle, Newspaper } from 'lucide-react';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import { useApi } from '../../lib/api';
import { useAuth } from '../../lib/auth.context';
import { useBatchPlayerDetails } from '../../hooks/usePlayerDetails';
// import { NewsButton } from '../news/NewsButton';
import { NewsResultsDialog } from '../news/NewsResultsDialog';
import { useKYPSearch } from '../../lib/kyp-search';
import { useInjuryRequestService } from '../../lib/injury-request.service';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Injury Info Button component
const InjuryInfoButton = ({ 
  playerId, 
  playerName, 
  playerClub 
}: { 
  playerId: string; 
  playerName: string; 
  playerClub: string;
}) => {
  const { playerInjuries } = useKYPSearch();
  const { requestInjuryInfo } = useInjuryRequestService();
  const [isRequestingInfo, setIsRequestingInfo] = useState(false);
  
  // Check if player has active injuries
  const activeInjuries = playerInjuries?.filter(
    injury => injury.player_id === playerId && 
    (!injury.until_date || new Date(injury.until_date) > new Date())
  ) || [];
  
  // If no active injuries, don't render anything
  if (activeInjuries.length === 0) return null;
  
  // Get injury details
  const injuryDetails = activeInjuries.map(injury => injury.injury).join(', ');
  
  // Get injury date
  const injuryDate = activeInjuries.length > 0 
    ? new Date(activeInjuries[0].from_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : '';
  
  // Handle requesting more information
  const handleRequestMoreInfo = async () => {
    if (!injuryDetails || !injuryDate) return;
    
    setIsRequestingInfo(true);
    try {
      await requestInjuryInfo(
        playerId,
        playerName,
        playerClub,
        injuryDetails,
        injuryDate
      );
    } finally {
      setIsRequestingInfo(false);
    }
  };
  
  return (
    <div className="mt-2">
      <div className="flex items-center text-xs text-[#4E46FF] mb-2 bg-[#26222E] p-2 rounded-lg border border-gray-500">
        <AlertCircle className="h-3 w-3 mr-1" />
        <span className="font-medium">Active Injury: {injuryDetails}</span>
      </div>
      <Button
        variant="ghost"
        size="sm"
        className={`h-7 px-2 py-0 text-xs w-full rounded-lg ${
          isRequestingInfo 
            ? "bg-[#26222E] text-[#CECFD2] opacity-50" 
            : "bg-[#4E46FF] text-white hover:bg-[#3d37cc]"
        }`}
        onClick={handleRequestMoreInfo}
        disabled={isRequestingInfo}
      >
        {isRequestingInfo ? (
          <>
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            Requesting...
          </>
        ) : (
          'Request More Information'
        )}
      </Button>
    </div>
  );
};

// Sortable item component
const SortableWishlistItem = ({ 
  item, 
  player, 
  onRemove,
  onKypClick: onNewsClick // Renamed prop internally for clarity while maintaining external compatibility
}: { 
  item: { playerId: string; index: number; createdAt: string }; 
  player: any; 
  onRemove: (playerId: string, index: number) => void;
  onKypClick: (playerId: string, playerName: string) => void; // Keeping the same prop name for compatibility
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.index.toString() });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 1,
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <div 
      ref={setNodeRef}
      style={style}
      className={`p-2 rounded-lg bg-black border border-gray-500 hover:bg-gray-900 transition-colors ${isDragging ? 'shadow-lg' : ''} group`}
    >
      {/* Player info section */}
      <div className="mb-2">
        {/* Top row with photo and name */}
        <div className="flex items-center mb-3">
          <div 
            className="flex-shrink-0 mr-2 cursor-grab text-gray-500 hover:text-gray-300"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-5 w-5" />
          </div>
          
          <div className="flex-shrink-0 mr-2">
            <img 
              src={player.image_url || '/footballer.png'} 
              alt={player.name}
              className="rounded-full object-cover"
              style={{ width: '3.75rem', height: '3.75rem' }} /* 50% larger than 2.5rem (w-10) */
              onError={(e) => {
                // Replace broken image with initials
                e.currentTarget.style.display = 'none';
                e.currentTarget.nextElementSibling?.classList.remove('hidden');
              }}
            />
            <div 
              className="hidden rounded-full bg-gray-700 flex items-center justify-center"
              style={{ width: '3.75rem', height: '3.75rem' }} /* 50% larger than 2.5rem (w-10) */
            >
              <span className="text-gray-300 text-sm">
                {player.name.substring(0, 2).toUpperCase()}
              </span>
            </div>
          </div>
          
          <div className="flex-grow min-w-0">
            <p className="text-sm font-medium text-gray-100 truncate">
              {player.name}
            </p>
          </div>
        </div>
        
        {/* Metadata row below */}
        <div className="pl-7">
          <p className="text-xs text-gray-400">
            {player.position} • {player.club_name}
          </p>
        </div>
      </div>
      
      {/* Divider between card content and buttons */}
      <div className="my-2 border-t border-gray-600"></div>
      
      {/* Bottom row with buttons */}
      <div className="flex items-center justify-start space-x-2 pl-2">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-3 py-2 text-xs text-red-500 hover:text-red-400 bg-transparent hover:bg-[#26222E] rounded-md flex items-center gap-1 border-none"
          onClick={() => onRemove(item.playerId, item.index)}
          title="Remove from shortlist"
          style={{ backgroundColor: 'transparent' }}
        >
          <Trash2 className="h-3 w-3" />
          <span>Remove</span>
        </Button>
        
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-3 py-2 text-xs font-bold text-white bg-transparent hover:bg-[#26222E] rounded-md flex items-center gap-1 border-none"
          onClick={() => onNewsClick(item.playerId, player.name)}
          title={`View news articles for ${player.name}`}
          style={{ backgroundColor: 'transparent' }}
        >
          <Newspaper className="h-3 w-3" />
          <span>News</span>
        </Button>
      </div>
      
      {/* Injury Info Button - only shown if player has active injuries */}
      <InjuryInfoButton 
        playerId={item.playerId}
        playerName={player.name}
        playerClub={player.club_name || 'Unknown'}
      />
    </div>
  );
};

// Loading placeholder for wishlist items
const WishlistItemPlaceholder = ({ key }: { key: string }) => (
  <div 
    key={key}
    className="p-2 rounded-lg bg-black border border-gray-500 transition-colors"
  >
    <div className="mb-2">
      {/* Top row with photo and name */}
      <div className="flex items-center mb-3">
        <div className="flex-shrink-0 mr-2">
          <div className="w-5 h-5 bg-gray-700 rounded animate-pulse"></div>
        </div>
        <div className="flex-shrink-0 mr-2">
          <div 
            className="rounded-full bg-gray-700 flex items-center justify-center animate-pulse"
            style={{ width: '3.75rem', height: '3.75rem' }} /* 50% larger than 2.5rem (w-10) */
          >
          </div>
        </div>
        <div className="flex-grow min-w-0">
          <div className="h-4 bg-gray-700 rounded w-3/4 animate-pulse"></div>
        </div>
      </div>
      
      {/* Metadata row below */}
      <div className="pl-7">
        <div className="h-3 bg-gray-700 rounded w-1/2 animate-pulse"></div>
      </div>
    </div>
    {/* Divider between card content and buttons */}
    <div className="my-2 border-t border-gray-600"></div>
    
    <div className="flex items-center justify-start space-x-2 pl-2">
      <div className="h-7 w-20 bg-transparent rounded-md animate-pulse flex items-center">
        <div className="h-3 w-3 bg-red-500 rounded-full mr-1"></div>
        <div className="h-2 w-12 bg-red-500 rounded"></div>
      </div>
      <div className="h-7 w-16 bg-transparent rounded-md animate-pulse flex items-center">
        <div className="h-3 w-3 bg-white rounded-full mr-1"></div>
        <div className="h-2 w-8 bg-white rounded"></div>
      </div>
    </div>
  </div>
);

export const WishlistPanel = () => {
  const { defaultWishlist, fetchLists, reorderWishlistItem } = usePlayerListContext();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);
  const { toast } = useToast();
  const api = useApi();
  const { user } = useAuth();
  
  // State for News dialog
  const [selectedPlayer, setSelectedPlayer] = useState<{ id: string; name: string } | null>(null);
  const [isNewsDialogOpen, setIsNewsDialogOpen] = useState(false);
  
  // Extract player IDs from the wishlist
  const playerIds = defaultWishlist?.items.map(item => item.playerId) || [];
  const uniquePlayerIds = [...new Set(playerIds)];
  
  // Only fetch player details if we have a wishlist with items
  const shouldFetchPlayers = !!defaultWishlist && uniquePlayerIds.length > 0;
  
  // Use the batch player details hook with conditional fetching
  const { 
    data: playerDetails = {}, 
    isLoading: isLoadingPlayers, 
    error: playerError 
  } = useBatchPlayerDetails(shouldFetchPlayers ? uniquePlayerIds : []);

  // Set up DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Minimum drag distance before activation
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Memoize the fetchLists function to prevent it from causing infinite loops
  const fetchWishlist = useCallback(async () => {
    if (hasAttemptedFetch) return; // Prevent multiple fetch attempts if there was an error
    
    setIsLoading(true);
    setHasAttemptedFetch(true);
    
    try {
      console.time('PERF:WishlistPanel:fetchLists');
      await fetchLists();
      console.timeEnd('PERF:WishlistPanel:fetchLists');
      setError(null);
      
      // Log when we have the wishlist data but before player details are loaded
      if (defaultWishlist) {
        console.log(`PERF:WishlistPanel: Wishlist data received with ${defaultWishlist.items.length} items, now waiting for player details`);
      }
    } catch (error) {
      console.error('Error fetching wishlist:', error);
      setError('Unable to load your shortlist. Please try again later.');
      
      // Show toast only once
      toast({
        title: 'Error',
        description: 'Failed to load your shortlist',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [fetchLists, hasAttemptedFetch, toast, defaultWishlist]);
  
  // Only fetch lists once when component mounts
  useEffect(() => {
    fetchWishlist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRemovePlayer = useCallback(async (playerId: string, index: number) => {
    if (!user?.id || !defaultWishlist) return;
    
    try {
      await api.delete(`/api/v1/users/${user.id}/lists/${defaultWishlist.id}/items/${index}`);
      
      // Refresh the lists
      await fetchLists();
      
      toast({
        title: 'Player Removed',
        description: 'Player has been removed from your shortlist',
        variant: 'destructive',
        className: 'bg-green-100 dark:bg-green-900 border-green-200 dark:border-green-800 text-green-900 dark:text-green-100',
      });
    } catch (error) {
      console.error('Error removing player:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove player from shortlist',
        variant: 'destructive',
      });
    }
  }, [user?.id, defaultWishlist, api, toast, fetchLists]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;
    
    const currentIndex = parseInt(active.id.toString());
    const newIndex = parseInt(over.id.toString());
    
    if (isNaN(currentIndex) || isNaN(newIndex)) return;
    
    // Log the reordering operation for debugging
    console.log(`Reordering item from index ${currentIndex} to ${newIndex}`);
    console.log(`Current items:`, defaultWishlist?.items);
    
    // Call the reorderWishlistItem function from the context
    reorderWishlistItem(currentIndex, newIndex)
      .then(result => {
        if (result) {
          console.log(`Reordering successful. New items:`, result.items);
        } else {
          console.error(`Reordering failed`);
        }
      })
      .catch(error => {
        console.error(`Error during reordering:`, error);
      });
  }, [reorderWishlistItem, defaultWishlist]);

  // Sort the items by index
  const sortedItems = defaultWishlist?.items.sort((a, b) => a.index - b.index) || [];
  
  // Create an array of item IDs for the SortableContext
  const itemIds = sortedItems.map(item => item.index.toString());
  
  // Handle News button click
  const handleNewsClick = (playerId: string, playerName: string) => {
    setSelectedPlayer({ id: playerId, name: playerName });
    setIsNewsDialogOpen(true);
  };

  return (
    <ErrorBoundary>
      <div className="bg-black rounded-lg shadow-lg p-4 h-full transition-colors flex flex-col">
        <h2 className="text-xl font-semibold mb-4 text-gray-100">Shortlist</h2>
        
        {/* News Results Dialog */}
        {selectedPlayer && (
          <NewsResultsDialog
            playerId={selectedPlayer.id}
            playerName={selectedPlayer.name}
            isOpen={isNewsDialogOpen}
            onClose={() => {
              setIsNewsDialogOpen(false);
              setSelectedPlayer(null);
            }}
          />
        )}
        
        {isLoading || isLoadingPlayers ? (
          <div className="flex justify-center items-center h-40">
            <Loader2 className="h-8 w-8 animate-spin text-blue-400" />
          </div>
        ) : error ? (
          <div className="space-y-3">
            <p className="text-red-400">Error loading shortlist</p>
            <p className="text-sm text-gray-300">
              {error}
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => {
                setError(null);
                setHasAttemptedFetch(false);
                fetchWishlist();
              }}
              className="mt-2 text-gray-200 border-gray-600 hover:bg-gray-800"
            >
              Retry
            </Button>
          </div>
        ) : playerError ? (
          <div className="space-y-3">
            <p className="text-red-400">Error loading player details</p>
            <p className="text-sm text-gray-300">
              {playerError instanceof Error ? playerError.message : 'Unknown error'}
            </p>
          </div>
        ) : !defaultWishlist || defaultWishlist.items.length === 0 ? (
          <div className="space-y-3">
            <p className="text-gray-300">Your shortlist is empty</p>
            <p className="text-sm text-gray-400">
              Add players from the Search page
            </p>
          </div>
        ) : (
          <div className="space-y-3 flex-1 overflow-y-auto pr-1">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={itemIds}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2">
                  {sortedItems.map((item) => {
                    const player = playerDetails[item.playerId];
                    
                    if (!player) {
                      return <WishlistItemPlaceholder key={`${item.playerId}-${item.index}-loading`} />;
                    }
                    
                    return (
                      <SortableWishlistItem
                        key={`${item.playerId}-${item.index}`}
                        item={item}
                        player={player}
                        onRemove={handleRemovePlayer}
                        onKypClick={handleNewsClick}
                      />
                    );
                  })}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
};